import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import styles from "@/components/anchorData/genericAccordion.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";

interface GenericAccordionItemProps {
  title: string;
  content: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const GenericAccordionItem: React.FC<GenericAccordionItemProps> = ({
  title,
  content,
  isOpen,
  onToggle,
}) => {
  return (
    <div className={styles.accordion_item}>
      <GenericButton
        variant="outlined"
        onClick={onToggle}
        className={styles.accordion_trigger}
        endIcon={isOpen ? "−" : "+"}
      >
        {title}
      </GenericButton>
      {isOpen && (
        <AnimatePresence initial={false}>
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={
              isOpen
                ? { height: "auto", opacity: 1 }
                : { height: 0, opacity: 0 }
            }
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={styles.accordion_contentWrapper}
          >
            <div className={styles.accordion_content}>{content}</div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

interface GenericAccordionProps {
  items: { title: string; content: React.ReactNode; hidden?: boolean }[];
  openIndices?: number[];
  onToggleIndex?: (idx: number, nextOpen: boolean) => void;
}

export const GenericAccordion: React.FC<GenericAccordionProps> = ({
  items,
  openIndices = [],
  onToggleIndex,
}) => {
  const [open, setOpen] = useState<boolean[]>(
    items.map((_, idx) => openIndices.includes(idx)),
  );
  const { t } = useTranslation();
  useEffect(() => {
    setOpen(items.map((_, i) => openIndices.includes(i)));
  }, [openIndices, items.length]);

  const toggleItem = (idx: number) => {
    setOpen((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      onToggleIndex?.(idx, next[idx]);
      return next;
    });
  };
  return (
    <div className={styles.accordion}>
      {items.map((item, idx) => (
        <div
          key={idx}
          style={{
            display: item.hidden ? "none" : "block",
            borderRadius: "0.75rem",
          }}
        >
          <GenericAccordionItem
            key={idx}
            title={t(`anchordata_sub_${item.title}`)}
            content={item.content}
            isOpen={open[idx]}
            onToggle={() => toggleItem(idx)}
          />
        </div>
      ))}
    </div>
  );
};
